// The live-event client (ADR 0005).
//
// One browser holds one connection, however many tabs are open. Tabs elect a
// leader with the Web Locks API; the leader opens the WebSocket and repeats
// every event to the other tabs over a BroadcastChannel. When the leader tab
// closes, its lock is released and another tab takes over.
//
// Every browser API is injected, so the logic is testable without one.

import { backoffDelay } from './backoff';
import {
  type ClientFrame,
  CloseCode,
  parseEnvelope,
  PROTOCOL_VERSION,
  type RealtimeEnvelope,
  type RealtimeStatus,
} from './protocol';

/** A WebSocket, as far as this client uses one. */
export interface SocketLike {
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

/** A broadcast channel, as far as this client uses one. */
export interface ChannelLike {
  onmessage: ((ev: { data: unknown }) => void) | null;
  postMessage(data: unknown): void;
  close(): void;
}

export interface RealtimeDeps {
  /** Buys a single-use ticket: POST /api/v1/realtime/tickets. */
  fetchTicket: () => Promise<string>;
  openSocket: (url: string) => SocketLike;
  socketUrl: string;
  /** null where BroadcastChannel is unavailable: every tab is then its own leader. */
  openChannel: ((name: string) => ChannelLike) | null;
  /** null where Web Locks are unavailable: likewise. */
  requestLeadership: ((name: string, holder: () => Promise<void>) => void) | null;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  random?: () => number;
  now?: () => number;
  /**
   * Whether the person used the app in the last few minutes. Sent with the
   * ticket, so they show as Active straight away rather than Away until their
   * next click (ADR 0008).
   */
  isActive?: () => boolean;
}

type Listener = (env: RealtimeEnvelope) => void;
type StatusListener = (status: RealtimeStatus) => void;

/** Consecutive attempts that never opened before the UI says "fallback". */
const FALLBACK_AFTER = 4;
/** A connection live this long counts as healthy, and resets the backoff. */
const HEALTHY_AFTER_MS = 30_000;
/** Send an application ping this often, so a dead connection is noticed. */
const PING_EVERY_MS = 25_000;
/** With nothing received for this long, assume the connection is dead. */
const SILENCE_LIMIT_MS = 60_000;

const LOCK_NAME = 'hrms-realtime-leader';
const CHANNEL_NAME = 'hrms-realtime';

type ChannelMessage =
  | { kind: 'event'; env: RealtimeEnvelope }
  | { kind: 'status'; status: RealtimeStatus }
  /** A follower tab asking the leader to send a frame to the server. */
  | { kind: 'send'; frame: ClientFrame };

export class RealtimeClient {
  private readonly deps: RealtimeDeps;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private status: RealtimeStatus = 'idle';

  private running = false;
  private leader = false;
  private channel: ChannelLike | null = null;
  private releaseLeadership: (() => void) | null = null;

  private socket: SocketLike | null = null;
  /** Drives the backoff delay. Reset only by a connection that stayed up. */
  private attempt = 0;
  /** Attempts in a row that never reached "hello"; drives "fallback". */
  private neverOpened = 0;
  private liveSince: number | null = null;
  private reconnectTimer: unknown = null;
  private pingTimer: unknown = null;
  private silenceTimer: unknown = null;

  constructor(deps: RealtimeDeps) {
    this.deps = deps;
  }

  /** Begin: join the tab election, and connect if this tab wins. */
  start(): void {
    if (this.running) return;
    this.running = true;

    if (this.deps.openChannel) {
      this.channel = this.deps.openChannel(CHANNEL_NAME);
      this.channel.onmessage = (ev) => this.onChannelMessage(ev.data as ChannelMessage);
    }

    if (this.deps.requestLeadership) {
      this.setStatus('connecting');
      this.deps.requestLeadership(LOCK_NAME, () => this.lead());
    } else {
      void this.lead();
    }
  }

  /** Stop everything — on sign-out. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.teardownSocket();
    this.clearTimers();
    this.releaseLeadership?.();
    this.releaseLeadership = null;
    this.leader = false;
    this.channel?.close();
    this.channel = null;
    this.setStatus('idle');
  }

  /** Listen for every event. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onStatus(fn: StatusListener): () => void {
    this.statusListeners.add(fn);
    fn(this.status);
    return () => this.statusListeners.delete(fn);
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  /**
   * Send a frame to the server, from whichever tab: the leader sends it, and a
   * follower hands it to the leader. Dropped when not live — callers that need
   * a frame to survive a reconnect send it again when the status returns to
   * live, which they hear about in every tab.
   */
  send(frame: ClientFrame): void {
    if (!this.running) return;
    if (this.leader) {
      this.sendNow(frame);
    } else {
      this.channel?.postMessage({ kind: 'send', frame } satisfies ChannelMessage);
    }
  }

  /** Whether this tab holds the connection. For tests and diagnostics. */
  isLeader(): boolean {
    return this.leader;
  }

  // ---- leadership --------------------------------------------------------

  /** Holds leadership until stopped: resolving the promise releases the lock. */
  private lead(): Promise<void> {
    if (!this.running) return Promise.resolve();
    this.leader = true;
    this.attempt = 0;
    this.neverOpened = 0;
    void this.connect();
    return new Promise<void>((resolve) => {
      this.releaseLeadership = resolve;
    });
  }

  private onChannelMessage(msg: ChannelMessage): void {
    if (msg.kind === 'send') {
      if (this.leader) this.sendNow(msg.frame);
      return;
    }
    if (this.leader) return; // the leader is the source, not a follower
    if (msg.kind === 'event') this.dispatch(msg.env);
    if (msg.kind === 'status') this.setStatus(msg.status, false);
  }

  // ---- connection --------------------------------------------------------

  private async connect(): Promise<void> {
    if (!this.running || !this.leader) return;
    if (this.attempt === 0) this.setStatus('connecting');
    this.liveSince = null;

    let ticket: string;
    try {
      ticket = await this.deps.fetchTicket();
    } catch {
      this.scheduleReconnect();
      return;
    }
    if (!this.running || !this.leader) return;

    const socket = this.deps.openSocket(this.deps.socketUrl);
    this.socket = socket;

    socket.onopen = () => {
      // The ticket goes in the first frame, never in the URL (ADR 0005).
      const auth: Record<string, unknown> = { type: 'auth', ticket };
      if (this.deps.isActive?.()) auth.active = true;
      socket.send(JSON.stringify(auth));
    };

    socket.onmessage = (ev) => {
      const env = parseEnvelope(ev.data);
      if (!env) return;
      this.resetSilence();

      if (env.v !== PROTOCOL_VERSION) {
        // A server speaking a newer protocol than this build. Polling keeps
        // the data right until the page is reloaded with the new build.
        this.teardownSocket();
        this.setStatus('fallback');
        return;
      }
      if (env.type === 'hello') {
        this.liveSince = this.now();
        this.neverOpened = 0;
        this.setStatus('live');
        this.startPing();
        return;
      }
      if (env.type === 'pong') return;

      this.dispatch(env);
      this.channel?.postMessage({ kind: 'event', env } satisfies ChannelMessage);
    };

    socket.onclose = (ev) => {
      if (this.socket !== socket) return; // an old socket we already replaced
      this.socket = null;
      this.clearTimers();

      if (ev.code === CloseCode.Unauthorised) {
        // The session is over (signed out elsewhere, suspended, exited). The
        // auth layer finds out on its next request; reconnecting would only
        // be refused again.
        this.setStatus('ended');
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // Every error is followed by a close, which decides what to do.
    };
  }

  private scheduleReconnect(): void {
    if (!this.running || !this.leader) return;

    // A connection that stayed up a while was healthy: start the backoff
    // again from the bottom. One that dropped straight after opening was not,
    // and a server that accepts then drops must not be hammered.
    if (this.liveSince !== null && this.now() - this.liveSince >= HEALTHY_AFTER_MS) {
      this.attempt = 0;
    }
    if (this.liveSince === null) this.neverOpened++;
    this.attempt++;

    this.setStatus(this.neverOpened >= FALLBACK_AFTER ? 'fallback' : 'reconnecting');
    const delay = backoffDelay(this.attempt, this.deps.random);
    this.reconnectTimer = this.deps.setTimer(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private sendNow(frame: ClientFrame): void {
    if (!this.socket || this.status !== 'live') return;
    try {
      this.socket.send(JSON.stringify(frame));
    } catch {
      // A send on a closing socket; the close handler takes over.
    }
  }

  private startPing(): void {
    this.clearTimer('pingTimer');
    const tick = () => {
      try {
        this.socket?.send(JSON.stringify({ type: 'ping' }));
      } catch {
        // A send on a closing socket; the close handler takes over.
      }
      this.pingTimer = this.deps.setTimer(tick, PING_EVERY_MS);
    };
    this.pingTimer = this.deps.setTimer(tick, PING_EVERY_MS);
    this.resetSilence();
  }

  private resetSilence(): void {
    this.clearTimer('silenceTimer');
    this.silenceTimer = this.deps.setTimer(() => {
      // Nothing — not even a pong — for a minute: the connection is dead even
      // if the browser has not noticed. Close it and let onclose reconnect.
      this.socket?.close(4000, 'silent');
    }, SILENCE_LIMIT_MS);
  }

  private teardownSocket(): void {
    const s = this.socket;
    this.socket = null;
    if (s) {
      s.onclose = null;
      s.onmessage = null;
      try {
        s.close(1000, 'client stopping');
      } catch {
        // already closed
      }
    }
  }

  private clearTimer(name: 'pingTimer' | 'silenceTimer' | 'reconnectTimer'): void {
    if (this[name] !== null) {
      this.deps.clearTimer(this[name]);
      this[name] = null;
    }
  }

  private clearTimers(): void {
    this.clearTimer('pingTimer');
    this.clearTimer('silenceTimer');
    this.clearTimer('reconnectTimer');
  }

  private now(): number {
    return (this.deps.now ?? Date.now)();
  }

  // ---- fan-out within this tab -------------------------------------------

  private dispatch(env: RealtimeEnvelope): void {
    for (const fn of this.listeners) {
      try {
        fn(env);
      } catch {
        // One broken listener must not stop the others.
      }
    }
  }

  private setStatus(status: RealtimeStatus, broadcast = true): void {
    if (this.status === status) return;
    this.status = status;
    for (const fn of this.statusListeners) fn(status);
    if (broadcast && this.leader) {
      this.channel?.postMessage({ kind: 'status', status } satisfies ChannelMessage);
    }
  }
}
